import { z } from 'zod';

import { type AnyZodSchema } from '@Common/types/zod/utils';
import {
  BrandedHashedIdParamsSchema,
  BrandedParamsSchema,
  BrandedRouteNotSpecifiedParams,
} from '@Common/types/zod/zod-params';

import type { DateString, DateTimeString } from '@Common/types/zod/zod-branded-date';

type HTTPMethods = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

type BrandedRouteParams = BrandedParamsSchema | BrandedHashedIdParamsSchema;

export interface EndpointConfigBase {
  method: HTTPMethods;
  headers?: AnyZodSchema;
  queryParams?: BrandedRouteParams;
  pathParams?: BrandedRouteParams;
  body?: AnyZodSchema;
  responseBody: AnyZodSchema;
}

type ZodOrUndefined<T, B> = [undefined] extends [T] ? B : T;

export interface EndpointConfig<T extends EndpointConfigBase> extends EndpointConfigBase {
  method: T['method'];
  headers: ZodOrUndefined<T['headers'], EndpointConfigBase['headers']>;
  queryParams: ZodOrUndefined<T['queryParams'], EndpointConfigBase['queryParams']>;
  pathParams: ZodOrUndefined<T['pathParams'], BrandedRouteNotSpecifiedParams>;
  body: ZodOrUndefined<T['body'], EndpointConfigBase['body']>;
  responseBody: T['responseBody'];
}

export function endpoint<T extends EndpointConfigBase>(config: T): EndpointConfig<T> {
  // Bail from unnecessary typechecking. Return type is explicitly annotated and config is typechecked on input
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return {
    ...config,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- same as above
  } as any;
}

type DateToString<Value> = Value extends Date ? string : Value;
type Primitive = string | number | boolean | bigint | symbol | null | undefined;

type DeepDateToString<Value> = Value extends Date | Primitive
  ? DateToString<Value>
  : Value extends Record<string, unknown> | unknown[]
    ? { [K in keyof Value]: DeepDateToString<Value[K]> }
    : Value;

type IsLiteral<Value> = [Value] extends [string | number | boolean]
  ? string extends Value
    ? false
    : number extends Value
      ? false
      : boolean extends Value
        ? false
        : true
  : false;

type HasSameLiterals<In, Out> = {
  [K in keyof In & keyof Out]: IsLiteral<In[K]> extends true
    ? [Out[K]] extends [In[K]]
      ? true
      : false
    : true;
}[keyof In & keyof Out];

/**
 * Variants of `Out` with the same literal values as `In`, e.g. the same discriminator. Without it, a
 * union `Out` only exposes the keys shared by all its variants.
 */
type MatchingOutVariant<In, Out> = Out extends unknown
  ? [HasSameLiterals<In, Out>] extends [true]
    ? Out
    : never
  : never;

type OutVariantOf<In, Out> = [MatchingOutVariant<In, Out>] extends [never]
  ? Out
  : MatchingOutVariant<In, Out>;

/**
 * Client side of a request value: the schema input with dates as strings, except where the schema
 * outputs a branded date. There the client must send that brand (nullability comes from the input),
 * because the input of a branded date schema is a plain `string | Date`.
 * Checks on `Out` are wrapped in tuples so a nullable output doesn't distribute into a plain string.
 * `In` does distribute, so each variant of a union is paired with its own variant of `Out`.
 */
type ClientRequestValue<In, Out> = [NonNullable<Out>] extends [never]
  ? DeepDateToString<In>
  : [NonNullable<Out>] extends [DateString | DateTimeString]
    ? NonNullable<Out> | Extract<In, null | undefined>
    : In extends Date | Primitive
      ? DateToString<In>
      : In extends readonly unknown[]
        ? [NonNullable<Out>] extends [readonly unknown[]]
          ? ClientRequestValue<In[number], NonNullable<Out>[number]>[]
          : DeepDateToString<In>
        : In extends Record<string, unknown>
          ? [NonNullable<Out>] extends [Record<string, unknown>]
            ? {
                [K in keyof In]: K extends keyof OutVariantOf<In, NonNullable<Out>>
                  ? ClientRequestValue<In[K], OutVariantOf<In, NonNullable<Out>>[K]>
                  : DeepDateToString<In[K]>;
              }
            : DeepDateToString<In>
          : In;

export type GetClientZodRequestType<T extends AnyZodSchema | undefined> = T extends undefined
  ? undefined
  : T extends BrandedParamsSchema | BrandedHashedIdParamsSchema
    ? DeepDateToString<z.input<T>>
    : ClientRequestValue<z.input<Exclude<T, undefined>>, z.output<Exclude<T, undefined>>>;

export type GetClientZodResponseType<T extends AnyZodSchema> = DeepDateToString<z.output<T>>;

interface EndpointTypeSideBase {
  RequestHeaders: unknown;
  RequestQueryParams: unknown;
  RequestPathParams: unknown;
  RequestBody: unknown;
  ResponseBody: unknown;
}

interface EndpointClientType<Config extends EndpointConfigBase> extends EndpointTypeSideBase {
  RequestHeaders: GetClientZodRequestType<Config['headers']>;
  RequestQueryParams: GetClientZodRequestType<Config['queryParams']>;
  RequestPathParams: GetClientZodRequestType<Config['pathParams']>;
  RequestBody: GetClientZodRequestType<Config['body']>;
  ResponseBody: GetClientZodResponseType<Config['responseBody']>;
}

type GetServerZodRequestType<Config extends AnyZodSchema | undefined> = Config extends AnyZodSchema
  ? z.output<Config>
  : undefined;

interface EndpointServerType<Config extends EndpointConfigBase> extends EndpointTypeSideBase {
  RequestHeaders: GetServerZodRequestType<Config['headers']>;
  RequestQueryParams: GetServerZodRequestType<Config['queryParams']>;
  RequestPathParams: GetServerZodRequestType<Config['pathParams']>;
  RequestBody: GetServerZodRequestType<Config['body']>;
  ResponseBody: z.input<Config['responseBody']>;
}

interface EndpointTypeBase {
  Client: EndpointTypeSideBase;
  Server: EndpointTypeSideBase;
}

export interface GetEndpointType<T extends EndpointConfigBase> extends EndpointTypeBase {
  Client: EndpointClientType<T>;
  Server: EndpointServerType<T>;
}

/* eslint-disable @typescript-eslint/no-explicit-any -- allowed here */
export function middlewareHandler<T extends EndpointConfigBase>(
  config: Partial<Pick<T, 'pathParams' | 'headers' | 'queryParams' | 'body'>>,
): EndpointConfig<T> {
  const conf: EndpointConfigBase = {
    pathParams: config.pathParams ?? undefined,
    headers: config.headers ?? undefined,
    queryParams: config.queryParams ?? undefined,
    body: config.body ?? undefined,
    method: 'GET',
    responseBody: z.any(),
  };

  // Bail from unnecessary typechecking. Return type is explicitly annotated and config is typechecked on input
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return endpoint(conf) as any;
}
